import type { components } from "@/api-schema";
import { PathsV1VehicleValuationPriceAdjustmentSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useVehicleValuationPriceAdjustmentCreateMutation } from "@/common/api/vehicle-valuation-price-adjustment/use-vehicle-valuation-price-adjustment-create.mutation";
import { useVehicleValuationPriceAdjustmentDeleteMutation } from "@/common/api/vehicle-valuation-price-adjustment/use-vehicle-valuation-price-adjustment-delete.mutation";
import { useVehicleValuationPriceAdjustmentSearchQuery } from "@/common/api/vehicle-valuation-price-adjustment/use-vehicle-valuation-price-adjustment-search.query";
import { useVehicleValuationPriceAdjustmentUpdateMutation } from "@/common/api/vehicle-valuation-price-adjustment/use-vehicle-valuation-price-adjustment-update.mutation";
import type { VehicleValuationPriceAdjustment } from "@/common/api/vehicle-valuation-price-adjustment/vehicle-valuation-price-adjustment.types";
import { useVehicleValuationSettingsGetQuery } from "@/common/api/vehicle-valuation-settings/use-vehicle-valuation-settings-get.query";
import { useVehicleValuationSettingsUpdateMutation } from "@/common/api/vehicle-valuation-settings/use-vehicle-valuation-settings-update.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputTextAlign } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { PencilIcon, PlusIcon, SaveIcon, Trash2Icon, XIcon } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "wouter";
import {
  EMPTY_PRICE_ADJUSTMENT_FORM,
  type PriceAdjustmentFormState,
  isOptionalSignedPercentage,
  isSignedPercentage,
  parseOptionalSignedPercentage,
  parseSignedPercentage,
  sortPriceAdjustments,
  toDraftNumber,
  toPriceAdjustmentFormState,
} from "../_helpers/fab-valuation-settings.helpers";
import {
  DefaultPriceAdjustmentSkeleton,
  PriceAdjustmentsListSkeleton,
} from "./fab-valuation-price-adjustments-skeleton";

type Brand = components["schemas"]["Brand"];

const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_11rem_5.5rem] items-center gap-5";

function isDefinedUuid(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function FabValuationPriceAdjustments(): ReactElement {
  const { t, i18n } = useTranslation(["fab", "common"]);
  const [searchParams] = useSearchParams();
  const conflictSearch = searchParams.get("search") ?? undefined;

  const settingsQuery = useVehicleValuationSettingsGetQuery();
  const settingsUpdateMutation = useVehicleValuationSettingsUpdateMutation();
  const priceAdjustmentsQuery = useVehicleValuationPriceAdjustmentSearchQuery({
    options: {
      body: {},
      parameters: {
        search: conflictSearch,
        pageSize: -1,
        extend: [PathsV1VehicleValuationPriceAdjustmentSearchPostParametersQueryExtend.Brand],
      },
    },
  });
  const brandSearchQuery = useBrandSearchQuery({
    options: {
      body: {},
      parameters: { pageSize: -1 },
    },
  });
  const priceAdjustmentCreateMutation = useVehicleValuationPriceAdjustmentCreateMutation();
  const priceAdjustmentUpdateMutation = useVehicleValuationPriceAdjustmentUpdateMutation();
  const priceAdjustmentDeleteMutation = useVehicleValuationPriceAdjustmentDeleteMutation();

  const [defaultAdjustmentDraft, setDefaultAdjustmentDraft] = useState<string | undefined>();
  const [newPriceAdjustment, setNewPriceAdjustment] = useState<PriceAdjustmentFormState>(EMPTY_PRICE_ADJUSTMENT_FORM);
  const [editingPriceAdjustmentUuid, setEditingPriceAdjustmentUuid] = useState<string | null>(null);
  const [editingPriceAdjustment, setEditingPriceAdjustment] =
    useState<PriceAdjustmentFormState>(EMPTY_PRICE_ADJUSTMENT_FORM);
  const [priceAdjustmentToDelete, setPriceAdjustmentToDelete] = useState<VehicleValuationPriceAdjustment | null>(null);
  const [activePriceAdjustmentAction, setActivePriceAdjustmentAction] = useState<string | null>(null);

  const priceAdjustments = useMemo(
    () => sortPriceAdjustments(priceAdjustmentsQuery.data?.data ?? []),
    [priceAdjustmentsQuery.data?.data],
  );

  const brands = useMemo(
    () =>
      [...(brandSearchQuery.data?.data ?? [])].sort((leftBrand, rightBrand) =>
        leftBrand.name.localeCompare(rightBrand.name, i18n.language),
      ),
    [brandSearchQuery.data?.data, i18n.language],
  );

  const configuredBrandUuids = useMemo(() => {
    const brandUuids = new Set<string>();

    for (const priceAdjustment of priceAdjustments) {
      if (isDefinedUuid(priceAdjustment.brand?.uuid)) {
        brandUuids.add(priceAdjustment.brand.uuid);
      }
    }

    return brandUuids;
  }, [priceAdjustments]);

  const percentageFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [i18n.language],
  );

  const defaultAdjustmentValue = defaultAdjustmentDraft ?? toDraftNumber(settingsQuery.data?.value);
  const isDefaultAdjustmentDirty = defaultAdjustmentDraft !== undefined;
  const isDefaultAdjustmentValid = isOptionalSignedPercentage(defaultAdjustmentValue);
  const showDefaultAdjustmentError = defaultAdjustmentValue.trim().length > 0 && !isDefaultAdjustmentValid;
  const isNewAdjustmentPercentageValid = isSignedPercentage(newPriceAdjustment.adjustmentPercentage);
  const isNewBrandValid = newPriceAdjustment.brand.trim().length > 0;
  const showNewAdjustmentPercentageError =
    newPriceAdjustment.adjustmentPercentage.trim().length > 0 && !isNewAdjustmentPercentageValid;
  const isEditingAdjustmentPercentageValid = isSignedPercentage(editingPriceAdjustment.adjustmentPercentage);
  const isEditingBrandValid = editingPriceAdjustment.brand.trim().length > 0;
  const showEditingAdjustmentPercentageError =
    editingPriceAdjustment.adjustmentPercentage.trim().length > 0 && !isEditingAdjustmentPercentageValid;

  function formatPercentage(value: number): string {
    return `${percentageFormatter.format(value)} %`;
  }

  function getBrandName(brand: Brand | null | undefined): string {
    return brand?.name ?? t("UnknownBrand");
  }

  function getBrandOptions(currentBrandUuid?: string): { label: string; value: string }[] {
    return brands
      .filter((brand) => brand.uuid === currentBrandUuid || !configuredBrandUuids.has(brand.uuid))
      .map((brand) => ({
        label: brand.name,
        value: brand.uuid,
      }));
  }

  function getPriceAdjustmentDisplayName(priceAdjustment: VehicleValuationPriceAdjustment): string {
    return `${getBrandName(priceAdjustment.brand)} · ${formatPercentage(priceAdjustment.adjustmentPercentage)}`;
  }

  function handleNewPriceAdjustmentChange(field: keyof PriceAdjustmentFormState, value: string): void {
    setNewPriceAdjustment((currentPriceAdjustment) => ({ ...currentPriceAdjustment, [field]: value }));
  }

  function handleStartPriceAdjustmentEdit(priceAdjustment: VehicleValuationPriceAdjustment): void {
    setEditingPriceAdjustmentUuid(priceAdjustment.uuid);
    setEditingPriceAdjustment(toPriceAdjustmentFormState(priceAdjustment));
  }

  function handleEditingPriceAdjustmentChange(field: keyof PriceAdjustmentFormState, value: string): void {
    setEditingPriceAdjustment((currentPriceAdjustment) => ({ ...currentPriceAdjustment, [field]: value }));
  }

  function handleCancelPriceAdjustmentEdit(): void {
    setEditingPriceAdjustmentUuid(null);
    setEditingPriceAdjustment(EMPTY_PRICE_ADJUSTMENT_FORM);
  }

  async function handleSaveDefaultAdjustment(): Promise<void> {
    if (!isDefaultAdjustmentValid) return;

    await handleFormSubmission(
      () =>
        settingsUpdateMutation.mutateAsync({
          value: parseOptionalSignedPercentage(defaultAdjustmentValue),
        }),
      {
        success: t("DefaultPriceAdjustmentSaved"),
        error: t("DefaultPriceAdjustmentSaveError"),
      },
      () => {
        setDefaultAdjustmentDraft(undefined);
      },
    );
  }

  async function handleCreatePriceAdjustment(): Promise<void> {
    if (!isNewBrandValid || !isNewAdjustmentPercentageValid) return;

    setActivePriceAdjustmentAction("create");
    await handleFormSubmission(
      () =>
        priceAdjustmentCreateMutation.mutateAsync({
          brand: newPriceAdjustment.brand,
          adjustmentPercentage: parseSignedPercentage(newPriceAdjustment.adjustmentPercentage),
        }),
      {
        success: t("PriceAdjustmentCreated"),
        error: t("PriceAdjustmentCreateError"),
        customErrors: { 409: t("PriceAdjustmentConflict") },
      },
      () => {
        setNewPriceAdjustment(EMPTY_PRICE_ADJUSTMENT_FORM);
      },
    );
    setActivePriceAdjustmentAction(null);
  }

  async function handleUpdatePriceAdjustment(): Promise<void> {
    if (!editingPriceAdjustmentUuid || !isEditingBrandValid || !isEditingAdjustmentPercentageValid) return;

    setActivePriceAdjustmentAction(`update:${editingPriceAdjustmentUuid}`);
    await handleFormSubmission(
      () =>
        priceAdjustmentUpdateMutation.mutateAsync({
          uuid: editingPriceAdjustmentUuid,
          brand: editingPriceAdjustment.brand,
          adjustmentPercentage: parseSignedPercentage(editingPriceAdjustment.adjustmentPercentage),
        }),
      {
        success: t("PriceAdjustmentSaved"),
        error: t("PriceAdjustmentSaveError"),
        customErrors: { 409: t("PriceAdjustmentConflict") },
      },
      () => {
        handleCancelPriceAdjustmentEdit();
      },
    );
    setActivePriceAdjustmentAction(null);
  }

  async function handleDeletePriceAdjustment(): Promise<void> {
    if (!priceAdjustmentToDelete) return;

    setActivePriceAdjustmentAction(`delete:${priceAdjustmentToDelete.uuid}`);
    await handleFormSubmission(
      () => priceAdjustmentDeleteMutation.mutateAsync({ uuid: priceAdjustmentToDelete.uuid }),
      { success: t("PriceAdjustmentDeleted"), error: t("PriceAdjustmentDeleteError") },
      () => {
        if (editingPriceAdjustmentUuid === priceAdjustmentToDelete.uuid) {
          handleCancelPriceAdjustmentEdit();
        }

        setPriceAdjustmentToDelete(null);
      },
    );
    setActivePriceAdjustmentAction(null);
  }

  return (
    <section id="price-adjustments" className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("PriceAdjustments")}</CardTitle>
          <CardDescription>{t("PriceAdjustmentsDescription")}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {settingsQuery.isLoading && !settingsQuery.data ? (
            <DefaultPriceAdjustmentSkeleton />
          ) : settingsQuery.isError && !settingsQuery.data ? (
            <p className="text-error text-sm">{t("DefaultPriceAdjustmentLoadError")}</p>
          ) : (
            <div className="flex flex-row justify-between">
              <div className="flex min-h-9 items-center">
                <label className="block text-sm leading-6 font-medium" htmlFor="defaultPriceAdjustmentPercentage">
                  {t("DefaultPriceAdjustment")}
                </label>
              </div>
              <div className="flex min-h-9 items-center space-x-2 md:justify-end">
                <div>
                  <LocalizedNumberInput
                    id="defaultPriceAdjustmentPercentage"
                    min="-100"
                    maxFractionDigits={2}
                    prefix="%"
                    textAlign={InputTextAlign.Right}
                    placeholder="0"
                    value={defaultAdjustmentValue}
                    onValueChange={(value) => setDefaultAdjustmentDraft(value)}
                    error={showDefaultAdjustmentError}
                    className="md:w-40"
                  />
                  {showDefaultAdjustmentError ? (
                    <p className="text-error text-xs">{t("EnterValidPercentage")}</p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant={
                    isDefaultAdjustmentDirty && !settingsUpdateMutation.isPending
                      ? ButtonVariant.Filled
                      : ButtonVariant.Outlined
                  }
                  colorVariant={
                    isDefaultAdjustmentDirty && !settingsUpdateMutation.isPending
                      ? ButtonColorVariant.Primary
                      : ButtonColorVariant.Secondary
                  }
                  size={ButtonSize.Sm}
                  aria-label={t("SavePriceAdjustment")}
                  disabled={!isDefaultAdjustmentDirty || !isDefaultAdjustmentValid || settingsUpdateMutation.isPending}
                  onClick={() => void handleSaveDefaultAdjustment()}
                >
                  {settingsUpdateMutation.isPending ? (
                    <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                  ) : (
                    <SaveIcon />
                  )}
                </Button>
              </div>
            </div>
          )}

          {priceAdjustmentsQuery.isLoading && !priceAdjustmentsQuery.data ? (
            <PriceAdjustmentsListSkeleton />
          ) : priceAdjustmentsQuery.isError && !priceAdjustmentsQuery.data ? (
            <p className="text-error text-sm">{t("PriceAdjustmentsLoadError")}</p>
          ) : (
            <div className="space-y-1.5">
              <label className="mb-4 block text-sm leading-6 font-medium">{t("PriceAdjustmentsPerBrand")}</label>

              <div className={`${ROW_GRID}`}>
                <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                  {t("common:Brand")}
                </p>
                <p className="text-muted-foreground text-right text-xs font-medium tracking-wider uppercase">
                  {t("PriceAdjustment")}
                </p>
                <div />
              </div>

              {priceAdjustments.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">{t("NoPriceAdjustmentsConfigured")}</p>
              ) : null}

              {priceAdjustments.map((priceAdjustment) => {
                const isEditing = editingPriceAdjustmentUuid === priceAdjustment.uuid;
                const isUpdating =
                  activePriceAdjustmentAction === `update:${priceAdjustment.uuid}` &&
                  priceAdjustmentUpdateMutation.isPending;
                const isDeleting =
                  activePriceAdjustmentAction === `delete:${priceAdjustment.uuid}` &&
                  priceAdjustmentDeleteMutation.isPending;
                const editingBrandOptions = getBrandOptions(priceAdjustment.brand?.uuid ?? undefined);

                return (
                  <div className={`${ROW_GRID} py-0.5`} key={priceAdjustment.uuid}>
                    <div>
                      {isEditing ? (
                        <SearchableSelect
                          id={`price-adjustment-brand-${priceAdjustment.uuid}`}
                          options={editingBrandOptions}
                          value={editingPriceAdjustment.brand}
                          placeholder={t("SelectBrand")}
                          searchPlaceholder={t("SearchBrand")}
                          emptyText={t("common:NoBrandsFound")}
                          clearable={false}
                          disabled={brandSearchQuery.isLoading || isUpdating}
                          onValueChange={(value) => handleEditingPriceAdjustmentChange("brand", value)}
                        />
                      ) : (
                        <span className="text-sm">{getBrandName(priceAdjustment.brand)}</span>
                      )}
                    </div>
                    <div>
                      {isEditing ? (
                        <>
                          <label className="sr-only" htmlFor={`price-adjustment-percentage-${priceAdjustment.uuid}`}>
                            {t("PriceAdjustment")}
                          </label>
                          <LocalizedNumberInput
                            id={`price-adjustment-percentage-${priceAdjustment.uuid}`}
                            min="-100"
                            maxFractionDigits={2}
                            prefix="%"
                            textAlign={InputTextAlign.Right}
                            error={showEditingAdjustmentPercentageError}
                            value={editingPriceAdjustment.adjustmentPercentage}
                            onValueChange={(value) => handleEditingPriceAdjustmentChange("adjustmentPercentage", value)}
                          />
                          {showEditingAdjustmentPercentageError ? (
                            <p className="text-error text-xs">{t("EnterValidPercentage")}</p>
                          ) : null}
                        </>
                      ) : (
                        <span className="block text-right text-sm">
                          {formatPercentage(priceAdjustment.adjustmentPercentage)}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            aria-label={t("SavePriceAdjustment")}
                            disabled={isUpdating || !isEditingBrandValid || !isEditingAdjustmentPercentageValid}
                            onClick={() => void handleUpdatePriceAdjustment()}
                          >
                            {isUpdating ? (
                              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                            ) : (
                              <SaveIcon />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            aria-label={t("CancelEdit")}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Secondary}
                            disabled={isUpdating}
                            onClick={handleCancelPriceAdjustmentEdit}
                          >
                            <XIcon />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            aria-label={t("EditPriceAdjustment")}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Secondary}
                            disabled={activePriceAdjustmentAction != null || isDeleting}
                            onClick={() => handleStartPriceAdjustmentEdit(priceAdjustment)}
                          >
                            <PencilIcon />
                          </Button>
                          <Button
                            type="button"
                            size={ButtonSize.Sm}
                            aria-label={t("DeletePriceAdjustment")}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Error}
                            disabled={activePriceAdjustmentAction != null || isDeleting}
                            onClick={() => setPriceAdjustmentToDelete(priceAdjustment)}
                          >
                            {isDeleting ? (
                              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                            ) : (
                              <Trash2Icon />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className={`${ROW_GRID} py-0.5`}>
                <div>
                  <SearchableSelect
                    id="newPriceAdjustmentBrand"
                    options={getBrandOptions()}
                    value={newPriceAdjustment.brand}
                    placeholder={t("SelectBrand")}
                    searchPlaceholder={t("SearchBrand")}
                    emptyText={t("common:NoBrandsFound")}
                    clearable={false}
                    disabled={brandSearchQuery.isLoading}
                    onValueChange={(value) => handleNewPriceAdjustmentChange("brand", value)}
                  />
                </div>
                <div>
                  <label className="sr-only" htmlFor="newPriceAdjustmentPercentage">
                    {t("PriceAdjustment")}
                  </label>
                  <LocalizedNumberInput
                    id="newPriceAdjustmentPercentage"
                    min="-100"
                    maxFractionDigits={2}
                    prefix="%"
                    textAlign={InputTextAlign.Right}
                    error={showNewAdjustmentPercentageError}
                    value={newPriceAdjustment.adjustmentPercentage}
                    onValueChange={(value) => handleNewPriceAdjustmentChange("adjustmentPercentage", value)}
                  />
                  {showNewAdjustmentPercentageError ? (
                    <p className="text-error text-xs">{t("EnterValidPercentage")}</p>
                  ) : null}
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size={ButtonSize.Sm}
                    aria-label={t("AddPriceAdjustment")}
                    disabled={
                      !isNewBrandValid ||
                      !isNewAdjustmentPercentageValid ||
                      (activePriceAdjustmentAction === "create" && priceAdjustmentCreateMutation.isPending)
                    }
                    onClick={() => void handleCreatePriceAdjustment()}
                  >
                    {activePriceAdjustmentAction === "create" && priceAdjustmentCreateMutation.isPending ? (
                      <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                    ) : (
                      <PlusIcon />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {priceAdjustmentToDelete ? (
        <DeleteConfirmDialog
          open
          label={t("PriceAdjustment")}
          name={getPriceAdjustmentDisplayName(priceAdjustmentToDelete)}
          onCancel={() => setPriceAdjustmentToDelete(null)}
          onConfirm={() => void handleDeletePriceAdjustment()}
        />
      ) : null}
    </section>
  );
}
